import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { format } from "date-fns";
import ExcelJS from "exceljs";
import { saveAs } from 'file-saver';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography,  
    CircularProgress, 
    Tooltip, 
    IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { fetchLegMembersByFlt } from 'state/api';
import { logClientAction } from 'state/logger';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import DataGridComponent from 'components/DataGridComponent';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import CrewListDialog from 'components/CrewListDialog';
import CardList from 'components/CardList';
import FlightFilterDialog from './FlightFilterDialog';


const hiddenColumns = {
    _id: false,
    acType: false,
    flightReg: false,
    flightType: false,
    doorClose: false,
    std: false,
    sta: false,
    atd: false,
    ata: false,
    takeOff: false,
    touchDown: false,
    stdLocalBase: false,
    staLocalBase: false,
    atdLocalBase: false,
    ataLocalBase: false,
    takeOffLocalBase: false,
    touchDownLocalBase: false,
};

const nonMobileHiddenColumns = {
    _id: false,
    flightType: false,
    std: false,
    sta: false,
    atd: false,
    ata: false,
    takeOff: false,
    touchDown: false,
    stdLocalBase: false,
    staLocalBase: false,
    atdLocalBase: false,
    ataLocalBase: false,
    takeOffLocalBase: false,
    touchDownLocalBase: false,
};


const FlightList = () => {
    useAuthCheck();
    const user = useSelector((state) => state.user); 
    const timeMode = useSelector((state) => state.timemode); 
    const crewRanksData = useSelector((state) => state.crewRanks);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [columnVisibilityModel, setColumnVisibilityModel] = useState(
        isDesktop ? nonMobileHiddenColumns : hiddenColumns
    );

    const { startDate, endDate } = useDateRangeContext();

    const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
    const canView = hasRight(permissionMap, "FlightList", "view", false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState({ isInvoiced: 'ALL' });
    const defaultFilters = {
        isInvoiced: 'ALL',
    };

    const [openCrewListDialog, setOpenCrewListDialog] = useState(false);

    const [flight, setFlight] = useState([]);
    const [crewList, setCrewList] = useState([]);
    const [jumpList, setJumpList] = useState([]);
    const [flights, setFlights] = useState([]);        
        

    
    useEffect(() => {
        const handleFlights = async () => {
            if (!startDate || !endDate) return [];
            
            try {         
                setError(null);  
                setLoading(true);

                const url = '/inflightsales/flightlist';
                const payload = { startDate, endDate }
                
                const response = await api.post(url, payload);
                const result = response.data;  
                
                setFlights(result);
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        handleFlights();      
    }, [startDate, endDate]); 
    
    const filteredFlights = useMemo(() => {
        if (!Array.isArray(flights)) {
            return []; 
        }
    
        if (!filters) return flights; 
    
        return flights.filter((flight) => {
            const flightEtdStr = timeMode === 'UTC' ? flight.etd?.substring(0, 5) : flight.etdLocalBase?.substring(0, 5);
            const minEtd = filters.minEtd;
            const maxEtd = filters.maxEtd;

            if (minEtd && (!flightEtdStr || flightEtdStr < minEtd)) return false;
            if (maxEtd && (!flightEtdStr || flightEtdStr > maxEtd)) return false;

            return Object.entries(filters).every(([key, value]) => {
                if (!value) return true; // If no value, don't filter on this key
                
                if (key === 'minEtd' || key === 'maxEtd') return true;
                
                // Handle date filtering
                if (key === 'flightDate') {
                    const formattedFlightDate = flight[key] ? format(new Date(flight[key]), 'yyyy-MM-dd') : null;
                    const formattedFilterDate = value ? format(new Date(value), 'yyyy-MM-dd') : null;
                    return formattedFlightDate === formattedFilterDate;
                }
    
                // Handle flight number filtering
                if (key === 'flightNo') {
                    return flight[key] === Number(value); 
                }

                if (key === 'acType' || key === 'flightReg') {
                    return Array.isArray(value) ? value.includes(flight[key]) : flight[key] === value;
                }

                // Handle isInvoiced filtering
                if (key === 'isInvoiced') {
                    if (value === 'ALL') return true; // Include all flights
                    if (value === 'INVOICED') return flight[key] === true; // Only invoiced flights
                    if (value === 'NOT_INVOICED') return flight[key] === false; // Only non-invoiced flights
                }
    
                // For other fields, continue normal comparison
                return flight[key] === value;
            });
        });
    }, [timeMode, flights, filters]);      

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };

    const handleCloseCrewListDialog = () => {
        setOpenCrewListDialog(false);
        setCrewList([]);
    };

    const handleViewClick = useCallback(async (f) => {
        try {      
            const {
                flightDetails, 
                legMembers, 
                jumpSeaters
            } = await fetchLegMembersByFlt(
                f.flightDate, 
                f.flightDesc, 
                f.dep
            );
            
            setFlight(flightDetails);
            setCrewList(legMembers);
            setJumpList(jumpSeaters);
            setOpenCrewListDialog(true);
        } catch (error) {
            setOpenCrewListDialog(false);
            toast.error(error?.message || "Failed to fetch crew members!");
        } 
    }, []);
        
    const handleExcelJSDownload = () => {
        const exportToExcel = async (data, fileName = "FlightList.xlsx") => {
            try {
                // 1. Create a new workbook
                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet("FlightList");

                // Add title row
                worksheet.addRow(["Flight List"]);
                worksheet.mergeCells("A1:M1");
                worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
                worksheet.getCell("A1").font = { size: 16, bold: true };

                // Add subtitle row
                const fullName = user?.firstName && user?.lastName ? user?.lastName + ' ' + user?.firstName : 'Unknown';
                const createdDate = `Reported by: ${fullName}, at: ${new Date().toLocaleString()}`;
                worksheet.addRow([createdDate]);
                worksheet.mergeCells("A2:M2");
                worksheet.getCell("A2").alignment = { horizontal: "center", vertical: "middle" };
                worksheet.getCell("A2").font = { italic: true };

                // Add subtitle row
                const timingMode = `Time mode: ${timeMode}`;
                worksheet.addRow([timingMode]);
                worksheet.mergeCells("A3:M3");
                worksheet.getCell("A3").alignment = { horizontal: "center", vertical: "middle" };
                worksheet.getCell("A3").font = { italic: true };

                // Add an empty row
                worksheet.addRow();
                
                // 2. Define column headers
                const headers = [
                    { header: "No", key: "No", width: 6 },
                    { header: "Flight Date", key: "FlightDate", width: 15 },
                    { header: "Flight No", key: "FlightNo", width: 10 },
                    { header: "Sector", key: "Sector", width: 10 },
                    { header: "ACType", key: "ACType", width: 10 },
                    { header: "FlightReg", key: "FlightReg", width: 10 },
                    { header: "Etd", key: "Etd", width: 8 },
                    { header: "Eta", key: "Eta", width: 8 },
                    { header: "Atd", key: "Atd", width: 8 },
                    { header: "Ata", key: "Ata", width: 8 },
                    { header: "Sector Type", key: "SectorType", width: 40 },
                    { header: "Crew List", key: "CrewList", width: 40 },
                    { header: "IsInvoiced", key: "IsInvoiced", width: 10 },
                ];

                worksheet.columns = headers;
                // Add headers
                worksheet.addRow(headers.map((col) => col.header));

                // 3. Add data rows
                data.forEach((row) => {
                    worksheet.addRow(row);
                });

                // 4. Apply styles (e.g., wrap text for certain columns)
                worksheet.eachRow((row, rowIndex) => {
                    row.eachCell((cell) => {
                        // Apply alignment and text wrapping
                        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
                        
                        // Apply border styles
                        cell.border = {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" },
                        };
                        
                        if (rowIndex === 4) {
                            // Style for headers
                            cell.font = { bold: true };
                            cell.fill = {
                                type: "pattern",
                                pattern: "solid",
                                fgColor: { argb: "FFFFCC" },
                            };
                        }
                    });
                });
                
                worksheet.getCell("A1").value = "Flight List";

                // 5. Generate and save the file
                const buffer = await workbook.xlsx.writeBuffer();
                saveAs(new Blob([buffer]), fileName); 
            } catch (error) {
                toast.error(error?.message || "Failed to export to Excel.");
            }
        };

        if (!flights || flights.length === 0) {
            toast.error("No data available to export.");
            return;
        }
                
        const transformedFlights = flights.map((flight, index) => {
            const crewList = flight.crewMembers
                .map(member => {
                    const rank = crewRanksData.find(rank => rank.rankCode === member.post) || {};
                    return { 
                        ...member, 
                        rankPriority: rank.rankPriority || Infinity, 
                        rankType: rank.rankType 
                    };
                })
                .filter(member => member.rankType === 'Cabin Crew') 
                .sort((a, b) => a.rankPriority - b.rankPriority) 
                .map(member => `${member.crewCode} ${member.crewName} ${member.post}`) 
                .join("\n");

            return {
                No: index + 1,
                FlightDate: timeMode === "UTC" ? format(flight.flightDate, "yyyy-MM-dd") : format(flight.flightDateLocalBase, "yyyy-MM-dd"),
                FlightNo: flight.flightNo,
                Sector: flight.sector,
                ACType: flight.acType,
                FlightReg: flight.flightReg,
                Etd: flight.etd,
                Eta: flight.eta,
                Atd: flight?.atd || '',
                Ata: flight?.ata || '',
                SectorType: flight?.sectorType || '',
                CrewList: crewList,
                IsInvoiced: flight.isInvoiced,
            };
        });

        // Add a timestamp to the filename
        const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "").slice(0, 14); // YYYYMMDDHHMMSS
        const fileName = `FlightList_${timestamp}.xlsx`;

        exportToExcel(transformedFlights, fileName);
    };

    const columns = useMemo(() => [
        {
            field: "no",
            headerName: "No",
            headerClassName: 'theme-column--header',
            filterable: false,      
            sortable: false,
            renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
            ...(() => {
                return isMobile ? { width: 60 } : { flex: 1 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "flightDate" : "flightDateLocalBase",
            headerName: "Date",
            headerClassName: 'theme-column--header',
            pinned: 'left',
            valueFormatter: (value) => {
                if (value == null) {
                  return '';
                }
                return `${format(value, "dd/MM/yy")}`;
            },
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1.5 };
            })(),
        },        
        {
            field: "flightNo",
            headerName: "FlightNo",
            pinned: 'left',
            headerClassName: 'theme-column--header',
            cellClassName: "name-column--cell",
            ...(() => {
                return isMobile ? { width: 85 } : { flex: 1.2 };
            })(),
        },        
        {
            field: "sector",
            headerName: "Sector",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 110 } : { flex: 1.5 };
            })(),
        },
        {
            field: "acType",
            headerName: "ACType",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1.3 };
            })(),
        },
        {
            field: "flightReg",
            headerName: "Reg",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 110 } : { flex: 1.5 };
            })(),
        },
        {
            field: "flightType",
            headerName: "FlightType",
            flex: 1,
            ...(() => {
                return isMobile ? { width: 150 } : { flex: 2.4 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "std" : "stdLocalBase",
            headerName: "Std",
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "sta" : "staLocalBase",
            headerName: "Sta",
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "etd" : "etdLocalBase",
            headerName: "Etd",
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "eta" : "etaLocalBase",
            headerName: "Eta",
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1 };
            })(),
        },        
        {
            field: timeMode === 'UTC' ? "atd" : "atdLocalBase",
            headerName: "Atd",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "takeOff" : "takeOffLocalBase",
            headerName: "TakeOff",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "touchDown" : "touchDownLocalBase",
            headerName: "TouchDown",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "ata" : "ataLocalBase",
            headerName: "Ata",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1 };
            })(),
        },       
        {
            field: 'sectorType',
            headerName: "SectorType",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 120 } : { flex: 1.6 };
            })(),
        }, 
        {
            field: 'crewMembers',
            headerName: 'CrewMembers',
            ...(() => {
                return isMobile ? { width: 330 } : { flex: 6 };
            })(),
            renderCell: (params) => {
                const sortedCrewMembers = (params?.row?.crewMembers || [])
                    ?.map(member => {
                        const rank = crewRanksData.find(r => r.rankCode === member.post);
                        return {
                            ...member,
                            rankPriority: rank?.rankPriority ?? Infinity,
                            rankType: rank?.rankType
                        };
                    })
                    ?.filter(member => member.rankType === 'Cabin Crew')
                    ?.sort((a, b) => a.rankPriority - b.rankPriority);
                    
                return (
                    <Box
                        sx={{
                            height: '100%',
                            alignItems: 'flex-start',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                        }}
                    >
                        {sortedCrewMembers?.map((member, index) => (
                            <Box
                                key={index}
                                display="flex"
                                alignItems="center"
                                alignContent='flex-start'
                                gap={1}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textAlign: 'right',
                                        whiteSpace: 'nowrap',
                                        width: '30px',
                                    }}
                                >
                                    {member.crewCode}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textAlign: 'left',
                                    }}
                                >
                                    {member.crewName}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textAlign: 'center',
                                    }}
                                >
                                    ({member.post} | {member.base})
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                );
            },
        },
        {
            field: "isInvoiced",
            headerName: "IsInvoiced",
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1.5 };
            })(),
        },
    ], [timeMode, isMobile, crewRanksData]);
    
    const actionColumn = useMemo(() => {
        const activeCount = [canView].filter(Boolean).length;
        if (activeCount === 0) return [];

        const baseWidth = 60 + activeCount * 25;

        return [{
            field: "action",
            headerName: "Action",
            filterable: false,
            sortable: false,
            width: baseWidth,
            renderCell: (params) => (
                <FlexCenter height="100%" gap={1}>
                    {canView && (
                        <Tooltip title="View">
                            <IconButton
                                aria-label="View"
                                sx={{ color: "primary.dark" }}
                                onClick={() => {
                                    handleViewClick(params.row);
                                }}
                            >
                                <VisibilityIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </FlexCenter>
            ),
        }];
    }, [canView, handleViewClick]);

    const columnsToShow = [...columns, ...actionColumn];
    
    const {
        acTypeOptions,
        flightRegOptions,
        // sectorTypeOptions,
        // departureTypeOptions,
        // arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(flights)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(flights.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [flights]);   

    
    return (
        <Box className="main-container-box">            
            <FlexBetween>
                <Header 
                    title="Flight List" 
                    subtitle={`${loading ? 'Loading...' : 'List of Flights'}`}
                /> 
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title="Download Excel file" arrow>
                            <span>
                                <StyledButton 
                                    onClick={() => {
                                        handleExcelJSDownload();
                                        logClientAction("EXPORT_REPORT", { fileName: "FlightList", format: "Excel", startDate, endDate });
                                    }} 
                                >
                                    <DownloadIcon />
                                </StyledButton>
                            </span>
                        </Tooltip>
                        <FilterButton
                            filters={filters}
                            defaultFilters={defaultFilters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}                
            </FlexBetween>              
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    gap={2}
                >
                    <Box
                        height={LAYOUT_HEIGHT}
                        sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
                    >
                        {isMobile ? (
                            <CardList
                                rows={filteredFlights}
                                primaryField="flightDesc"
                                secondaryField="sector"
                                fields={[
                                    { label: "Date", key: timeMode === 'UTC' ? "flightDate" : "flightDateLocalBase" },
                                    // { label: "Flight No", key: "flightNo" },
                                    { label: "AC Type", key: "acType" },
                                    { label: "AC Reg", key: "flightReg" },
                                    { label: "Etd", key: timeMode === 'UTC' ? "etd" : "etdLocalBase" },
                                    { label: "Eta", key: timeMode === 'UTC' ? "eta" : "etaLocalBase" },
                                    // { label: "Atd", key: timeMode === 'UTC' ? "atd" : "atdLocalBase" },
                                    // { label: "Ata", key: timeMode === 'UTC' ? "ata" : "ataLocalBase" },
                                    { label: "SectorType", key: "sectorType" },
                                    { label: "Members", key: "crewMembers" },
                                    { label: "IsInvoiced", key: "isInvoiced" },
                                ]}
                                isActiveField={null}
                                canView={canView}
                                canEdit={false}
                                canDelete={false}
                                onView={(id) => {
                                    const f = filteredFlights.find(x => x._id === id);
                                    if (f) handleViewClick(f);
                                }}
                            />
                        ) : (
                            <DataGridComponent
                                rows={filteredFlights || []}
                                columns={columnsToShow}
                                columnVisibilityModel={columnVisibilityModel}
                                setColumnVisibilityModel={setColumnVisibilityModel}
                                loading={loading}
                                props={{
                                    initialState: {
                                        pagination: { paginationModel: { pageSize: 100 } },
                                    },
                                    getRowClassName: (params) =>
                                        params.row.isInvoiced === false ? 'invoiced-false' : '',
                                }}
                                sx={{
                                    '& .MuiDataGrid-row.invoiced-false': {
                                        backgroundColor: `${theme.palette.primary.light} !important`,
                                    },
                                }}
                            />
                        )}
                    </Box>
                </Box>
            )}

            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions}
                    flightRegOptions={flightRegOptions}
                    currentFilters={filters}
                />
            }

            {openCrewListDialog &&
                <CrewListDialog
                    open={openCrewListDialog}
                    onClose={handleCloseCrewListDialog}
                    flight={flight}
                    crewList={crewList}
                    jumpList={jumpList}
                />
            }
        </Box>
    );
};

export default FlightList;
